import { useMutation, useQuery } from '@apollo/client';
import { StarBorderOutlined, WorkspacePremiumOutlined } from '@mui/icons-material';
import { Button, Stack, Tooltip } from '@mui/material';
import {
  useConfirmationDialog,
  useCustomSnackbar,
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetIcon,
} from '@procureai/ui-mui';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DEMAND_AWARDING_QUERY } from '../DemandAwardingQuery.gql';
import { useLineItemAssignments } from '../DemandAwardingWidget.hooks';
import { DEMAND_AWARD_NEGOTIATION_CASE } from '../DemandAwardNegotiationCase.gql';
import { TsrctDemandAwardInput, TsrctQuoteItemStatus } from '@/generated/gql/graphql';
import { getLineItemAwardingImpact } from '@/legacy/components/AwardingConfirmationDialog/AwardingConfirmationDialog.utils';
import { DemandLineItemAwardingConfirmationDialog } from '@/legacy/components/AwardingConfirmationDialog/DemandLineItemAwardingConfirmationDialog';
import { CircularProgressCentered } from '@/legacy/components/CircularProgressCentered/CircularProgressCentered';
import { I18nNamespace } from '~/common/types';
import { useConfig } from '~/components/ConfigProvider/ConfigProvider.context';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { MessageFormData } from '~/modules/DemandAwardingWidget/components/MessageForm/MessageForm.schema';

import { AwardingDataGridLineItemBidding } from './components/AwardingDataGrid/AwardingDataGridLineItemBidding';

export const DemandAwardingLineItemBiddingWidget = () => {
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandAwardingWidget' });
  const { features } = useConfig();

  const { data, loading: awardingQueryLoading } = useQuery(DEMAND_AWARDING_QUERY, {
    variables: {
      id: entityId,
    },
    skip: !entityId,
  });

  const [awardNegotiationCase, { loading: awardingMutationLoading }] = useMutation(DEMAND_AWARD_NEGOTIATION_CASE, {
    refetchQueries: [
      {
        query: DEMAND_AWARDING_QUERY,
        variables: {
          id: entityId,
        },
      },
    ],
  });

  const loading = awardingQueryLoading || awardingMutationLoading;
  const { onError, enqueueSuccessSnackbar } = useCustomSnackbar();
  const { confirm } = useConfirmationDialog();

  const demand = data?.tesseract.demand;
  const assignmentState = useLineItemAssignments(demand, features.awardableConfirmedQuotes);
  const selectedNegotiationCaseIds = useMemo(() => {
    const selectedCaseIds = new Set<string>();

    Object.values(assignmentState.value ?? {}).forEach((negotiationCaseId) => {
      if (negotiationCaseId) {
        selectedCaseIds.add(negotiationCaseId);
      }
    });

    return selectedCaseIds;
  }, [assignmentState.value]);

  const alreadyAwardedLineItemIds = useMemo(() => {
    if (!demand?.awardingSettings.isPartialAwardingEnabled) {
      return new Set<string>();
    }

    const demandRequestLineItems = demand.demandRequest?.lineItems ?? [];
    const awardedLineItemIds = new Set<string>();

    demand.negotiationCases.nodes.forEach((negotiationCase) => {
      negotiationCase.quote?.lineItems.forEach((lineItem, index) => {
        if (lineItem.status !== TsrctQuoteItemStatus.AWARDED) {
          return;
        }

        const demandRequestItemId =
          'demandRequestItemId' in lineItem ? lineItem.demandRequestItemId : demandRequestLineItems[index]?.id;

        if (demandRequestItemId) {
          awardedLineItemIds.add(demandRequestItemId);
        }
      });
    });

    return awardedLineItemIds;
  }, [demand]);

  const awardingImpact = useMemo(() => {
    const selectedCaseIds = demand?.awardingSettings.isPartialAwardingEnabled ? selectedNegotiationCaseIds : undefined;

    return getLineItemAwardingImpact(
      assignmentState.value,
      demand?.negotiationCases.nodes ?? [],
      alreadyAwardedLineItemIds,
      selectedCaseIds,
    );
  }, [
    assignmentState.value,
    demand?.awardingSettings.isPartialAwardingEnabled,
    demand?.negotiationCases.nodes,
    alreadyAwardedLineItemIds,
    selectedNegotiationCaseIds,
  ]);

  const submitAwardAssignedLineItemsToNegotiationCases = useCallback(
    async (formData: MessageFormData) => {
      if (!demand?.id || !assignmentState.value) {
        return;
      }

      try {
        const input: TsrctDemandAwardInput = {
          awards: Object.entries(assignmentState.value).map(([lineItemId, negotiationCaseId]) => ({
            demandRequestItemId: lineItemId,
            negotiationCaseId,
          })),
        };
        const isPartialAwardingEnabled = demand.awardingSettings.isPartialAwardingEnabled;

        if (demand.awardingSettings.notifications.options.informSupplier) {
          input.awardedSupplier = {
            informSupplier: formData.informAwardedSupplier,
            message: formData.informAwardedSupplier ? formData.message : undefined,
          };
          input.dismissedSupplier = {
            informSupplier: formData.informDismissedSuppliers,
          };
        }

        const { errors } = await awardNegotiationCase({
          variables: {
            id: demand.id,
            data: input,
          },
        });
        if (!errors) {
          if (isPartialAwardingEnabled) {
            assignmentState.resetLineItemAssignments();
          }
          enqueueSuccessSnackbar(t('actions.award.success'));
        }
      } catch (e) {
        onError(e);
      }
    },
    [assignmentState, demand, onError, t, awardNegotiationCase, enqueueSuccessSnackbar],
  );

  const awardAssignedLineItemsToNegotiationCases = useMemo(() => {
    if (!demand?.awardingSettings) {
      return () => {};
    }

    return confirm(submitAwardAssignedLineItemsToNegotiationCases, {
      title: t('awardDialog.title'),
      maxWidth: 'sm',
      content: (
        <DemandLineItemAwardingConfirmationDialog
          assignedAwardableLineItemsCount={awardingImpact.assignedAwardableLineItemsCount}
          dismissedAwardableLineItemsCount={awardingImpact.dismissedAwardableLineItemsCount}
          awardingSettings={demand.awardingSettings}
          numCases={demand.negotiationCases.totalCount}
        />
      ),
      confirm: {
        children: t('awardDialog.award'),
        color: 'primary',
      },
      cancel: {
        children: t('awardDialog.cancel'),
      },
    });
  }, [awardingImpact, confirm, demand, submitAwardAssignedLineItemsToNegotiationCases, t]);

  const awardingButton = (
    <Button
      disabled={loading || !assignmentState.isAtLeastOneLineItemAssigned}
      onClick={awardAssignedLineItemsToNegotiationCases}
      color="primary"
      variant="contained"
      loading={loading}
      startIcon={<WorkspacePremiumOutlined />}
    >
      {t('actions.award.label')}
    </Button>
  );

  return (
    <Widget hover={false}>
      <WidgetHeader
        title={t('title')}
        actionsRight={
          assignmentState.hasAssignableQuoteItems ? (
            !assignmentState.isAtLeastOneLineItemAssigned ? (
              <Tooltip title={t('actions.award.disabledTooltip')} arrow>
                <span>{awardingButton}</span>
              </Tooltip>
            ) : (
              awardingButton
            )
          ) : null
        }
        icon={<WidgetIcon icon={<StarBorderOutlined fontSize="small" />} />}
      />
      <WidgetContent disablePadding>
        {loading || !data?.tesseract.demand ? (
          <Stack position="relative" sx={{ height: '100px' }}>
            <CircularProgressCentered />
          </Stack>
        ) : (
          <AwardingDataGridLineItemBidding
            isMaskedBidding={data?.tesseract.demand.competitiveBiddingRule.isMaskedBidding}
            negotiationCases={data?.tesseract.demand.negotiationCases.nodes}
            awardingSettings={data?.tesseract.demand.awardingSettings}
            status={data?.tesseract.demand.awardingStatus}
            loading={loading}
            demand={data?.tesseract.demand}
            lineItemAssignments={assignmentState}
            awardableConfirmedQuotes={features.awardableConfirmedQuotes}
          />
        )}
      </WidgetContent>
    </Widget>
  );
};
